import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Save, User, Eye, Users, Cpu, ChevronDown, ChevronUp } from "lucide-react";
import {
  SocmintPanel, OsintPanel, HumintPanel,
  IntelligenceToolbar, buildOsintFilter, buildSocmintFilter, buildHumintFilter, buildSort,
} from "../../components/IntelligencePanels";
import { useLanguage } from "../../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SECTIONS = [
  {
    key: "osint",
    label: "OSINT",
    sublabel: "Open Source Intelligence",
    descKey: "intel.osint_desc_person",
    color: "#0891B2",
    icon: Eye,
  },
  {
    key: "socmint",
    label: "SOCMINT",
    sublabel: "Social Media Intelligence",
    descKey: "intel.socmint_desc_person",
    color: "#059669",
    icon: Users,
  },
  {
    key: "humint",
    label: "HUMINT",
    sublabel: "Human Intelligence",
    descKey: "intel.humint_desc_person",
    color: "#EA580C",
    icon: Cpu,
  },
];

function SectionCard({ section, children, count }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(true);
  return (
    <div
      data-testid={`intel-box-${section.key}`}
      className="bg-[#0D0F12] border border-white/[0.04]"
      style={{ borderTopColor: section.color, borderTopWidth: 2 }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 border-b border-white/[0.04] hover:bg-[#1A1D24] transition-colors"
      >
        <section.icon className="w-4 h-4 flex-shrink-0" style={{ color: section.color }} />
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold tracking-widest uppercase" style={{ fontFamily: "Chivo, sans-serif", color: section.color }}>
              {section.label}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 border" style={{ color: section.color, borderColor: `${section.color}40`, backgroundColor: `${section.color}10` }}>
              {count} {count === 1 ? t("intel.entry") : t("intel.entries")}
            </span>
          </div>
          <p className="text-xs text-[#A0AEC0] mt-0.5">{section.descKey ? t(section.descKey) : section.description}</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-[#A0AEC0]" /> : <ChevronDown className="w-4 h-4 text-[#A0AEC0]" />}
      </button>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
}

const EMPTY_INTEL = { osint: [], socmint: [], humint: [] };

export default function IntelligencePersonas({ scope = "sales", title, titleKey }) {
  const { t } = useLanguage();
  const resolvedTitle = titleKey ? t(titleKey) : (title || t(scope === "supplier" ? "intel.personas_title_supplier" : "intel.personas_title_sales"));
  const [targets, setTargets] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [intel, setIntel] = useState(EMPTY_INTEL);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ text: "", minImportance: 0, platform: "all", category: "all", channel: "all", sort: "default" });
  const [activeIntel, setActiveIntel] = useState("osint"); // osint | socmint | humint

  // filter/sort builders are module-level imports (stable references)
  const osintFilter = useMemo(() => buildOsintFilter(filters), [filters]);
  const socmintFilter = useMemo(() => buildSocmintFilter(filters), [filters]);
  const humintFilter = useMemo(() => buildHumintFilter(filters), [filters]);
  const sortFn = useMemo(() => buildSort(filters), [filters]);
  const counts = useMemo(() => ({
    osint:   { total: intel.osint.length,   visible: intel.osint.filter(osintFilter).length },
    socmint: { total: intel.socmint.length, visible: intel.socmint.filter(socmintFilter).length },
    humint:  { total: intel.humint.length,  visible: intel.humint.filter(humintFilter).length },
  }), [intel, osintFilter, socmintFilter, humintFilter]);

  useEffect(() => {
    // Reset state when scope changes (defensive: ensures Sales/Supplier independence)
    setTargets([]);
    setSelectedId("");
    setIntel(EMPTY_INTEL);
    axios
      .get(`${API}/intelligence/targets/personas`, { params: { scope }, withCredentials: true })
      .then(r => {
        setTargets(r.data);
        if (r.data.length > 0) setSelectedId(r.data[0].id);
      })
      .catch((err) => console.warn("[IntelligencePersonas] load targets failed:", err));
  }, [scope]);

  const loadIntel = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/intelligence/person/${id}`, { withCredentials: true });
      setIntel({
        osint: Array.isArray(data.osint) ? data.osint : [],
        socmint: Array.isArray(data.socmint) ? data.socmint : [],
        humint: Array.isArray(data.humint) ? data.humint : [],
      });
    } catch {
      setIntel(EMPTY_INTEL);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadIntel(selectedId); }, [selectedId, loadIntel]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/intelligence/person/${selectedId}`, intel, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  const selectedTarget = targets.find(tg => tg.id === selectedId);
  const totalEntries = intel.osint.length + intel.socmint.length + intel.humint.length;

  const [targetSearch, setTargetSearch] = useState("");
  const filteredTargets = useMemo(() => {
    const q = targetSearch.trim().toLowerCase();
    if (!q) return targets;
    return targets.filter(tg =>
      (tg.name || "").toLowerCase().includes(q) ||
      (tg.title || "").toLowerCase().includes(q) ||
      (tg.company || "").toLowerCase().includes(q));
  }, [targets, targetSearch]);

  const initials = (selectedTarget?.name || "?").trim().split(/\s+/).slice(0,2).map(s => s[0] || "").join("").toUpperCase();

  return (
    <div className="p-6 sm:p-8 space-y-5">
      {/* Hero header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 mb-2 text-[10px] tracking-[0.25em] uppercase text-[#00E5FF] font-bold">
            <Eye className="w-3 h-3" /> {scope === "supplier" ? "Supplier" : "Sales"} Monitor · People Watch
          </div>
          <h1 className="text-4xl font-bold text-white tracking-tight leading-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{resolvedTitle}</h1>
          <p className="text-sm text-[#A0AEC0] mt-1 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 bg-[#00E5FF] rounded-full" /> OSINT</span>
            <span className="text-[#30363D]">·</span>
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 bg-[#00FF66] rounded-full" /> SOCMINT</span>
            <span className="text-[#30363D]">·</span>
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 bg-[#FF9F0A] rounded-full" /> HUMINT</span>
          </p>
        </div>
        <button
          data-testid="save-intel-personas"
          onClick={handleSave}
          disabled={saving || !selectedId}
          className="flex items-center gap-2 bg-[#00E5FF] text-black font-bold px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-white transition-colors disabled:opacity-50"
        >
          <Save className="w-3.5 h-3.5" />
          {saving ? t("common.saving") : saved ? t("common.saved") : t("common.save_all")}
        </button>
      </div>

      {/* KPI bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="border border-white/[0.04] bg-[#0D0F12] p-4 flex items-center justify-between">
          <div>
            <p className="text-[9px] tracking-[0.2em] uppercase text-[#A0AEC0] font-semibold mb-1">Target totali</p>
            <p className="text-2xl font-bold text-white tabular-nums" style={{ fontFamily: "Chivo, sans-serif" }}>{targets.length}</p>
          </div>
          <User className="w-5 h-5 text-[#00E5FF]" />
        </div>
        <div className="border border-white/[0.04] bg-[#0D0F12] p-4 flex items-center justify-between">
          <div>
            <p className="text-[9px] tracking-[0.2em] uppercase text-[#00E5FF] font-semibold mb-1">OSINT entries</p>
            <p className="text-2xl font-bold tabular-nums" style={{ color: "#0891B2", fontFamily: "Chivo, sans-serif" }}>{intel.osint.length}</p>
          </div>
          <Eye className="w-5 h-5 text-[#00E5FF]" />
        </div>
        <div className="border border-white/[0.04] bg-[#0D0F12] p-4 flex items-center justify-between">
          <div>
            <p className="text-[9px] tracking-[0.2em] uppercase text-[#00FF66] font-semibold mb-1">SOCMINT entries</p>
            <p className="text-2xl font-bold tabular-nums" style={{ color: "#059669", fontFamily: "Chivo, sans-serif" }}>{intel.socmint.length}</p>
          </div>
          <Users className="w-5 h-5 text-[#00FF66]" />
        </div>
        <div className="border border-white/[0.04] bg-[#0D0F12] p-4 flex items-center justify-between">
          <div>
            <p className="text-[9px] tracking-[0.2em] uppercase text-[#FF9F0A] font-semibold mb-1">HUMINT entries</p>
            <p className="text-2xl font-bold tabular-nums" style={{ color: "#EA580C", fontFamily: "Chivo, sans-serif" }}>{intel.humint.length}</p>
          </div>
          <Cpu className="w-5 h-5 text-[#FF9F0A]" />
        </div>
      </div>

      {/* Master-detail: sidebar targets + main intelligence */}
      <div className="grid lg:grid-cols-[320px,1fr] gap-4">
        {/* SIDEBAR — target list */}
        <aside className="bg-[#0D0F12] border border-white/[0.04] flex flex-col" style={{ maxHeight: "70vh" }}>
          <div className="p-3 border-b border-white/[0.04]">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#A0AEC0] font-semibold mb-2 flex items-center justify-between">
              <span>Target list</span>
              <span className="text-[9px] tabular-nums text-[#6B7280]">{filteredTargets.length}/{targets.length}</span>
            </p>
            <input
              value={targetSearch} onChange={e => setTargetSearch(e.target.value)}
              placeholder="Cerca target…"
              className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF] placeholder:text-[#6B7280]"
            />
          </div>
          <div className="flex-1 overflow-y-auto p-1.5">
            {filteredTargets.map(tg => {
              const isSel = tg.id === selectedId;
              const init = (tg.name || "?").trim().split(/\s+/).slice(0,2).map(s => s[0] || "").join("").toUpperCase();
              return (
                <button
                  key={tg.id}
                  data-testid={`target-row-${tg.id}`}
                  onClick={() => setSelectedId(tg.id)}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 mb-1 text-left transition-colors border-l-2 ${isSel ? "bg-[#00E5FF]/10 border-[#00E5FF]" : "border-transparent hover:bg-[#12161C]"}`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center text-xs font-bold flex-shrink-0 ${isSel ? "bg-[#00E5FF] text-black" : "bg-[#1F242D] text-[#CBD5E1]"}`} style={{ fontFamily: "Chivo, sans-serif" }}>
                    {init}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold truncate ${isSel ? "text-white" : "text-[#CBD5E1]"}`}>{tg.name}</p>
                    <p className="text-[10px] text-[#6B7280] truncate">{tg.title || tg.company || "—"}</p>
                  </div>
                </button>
              );
            })}
            {filteredTargets.length === 0 && (
              <p className="text-xs text-[#6B7280] text-center py-6">Nessun target</p>
            )}
          </div>
        </aside>

        {/* MAIN — detail with intel sections */}
        <div className="min-w-0">
          {/* Selected target header */}
          {selectedTarget && (
            <div className="bg-[#0D0F12] border border-white/[0.04] p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-[#00E5FF] text-black font-bold text-base flex-shrink-0" style={{ fontFamily: "Chivo, sans-serif" }}>{initials}</div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg text-white font-bold leading-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{selectedTarget.name}</h2>
                  <p className="text-xs text-[#00E5FF] mt-0.5">{selectedTarget.title || "—"} {selectedTarget.company && <span className="text-[#CBD5E1]"> · {selectedTarget.company}</span>}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] uppercase tracking-widest text-[#A0AEC0]">Totale entries</p>
                  <p className="text-2xl font-bold text-white tabular-nums" style={{ fontFamily: "Chivo, sans-serif" }}>{totalEntries}</p>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-16 text-[#A0AEC0]">
              <div className="w-6 h-6 border-2 border-[#00E5FF] border-t-transparent animate-spin mx-auto mb-3" />
              <p className="text-xs tracking-widest uppercase">{t("intel.loading")}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <IntelligenceToolbar filters={filters} setFilters={setFilters} counts={counts} />

              {/* Tabs: OSINT / SOCMINT / HUMINT — one panel at a time */}
              <div className="bg-[#0D0F12] border border-white/[0.04] overflow-hidden">
                <div className="grid grid-cols-3 border-b border-white/[0.04]">
                  {SECTIONS.map((sec) => {
                    const key = sec.key.toLowerCase();
                    const isActive = activeIntel === key;
                    const c = counts[key] || { visible: 0, total: 0 };
                    return (
                      <button
                        key={sec.key}
                        data-testid={`intel-tab-${key}`}
                        onClick={() => setActiveIntel(key)}
                        className="relative flex items-center justify-center gap-2 py-4 transition-colors hover:bg-[#12161C]"
                        style={{
                          borderBottom: `3px solid ${isActive ? sec.color : "transparent"}`,
                          background: isActive ? `${sec.color}10` : undefined,
                        }}
                      >
                        <sec.icon className="w-4 h-4" style={{ color: isActive ? sec.color : "#A0AEC0" }} />
                        <span className="text-sm font-bold tracking-widest uppercase" style={{ fontFamily: "Chivo, sans-serif", color: isActive ? sec.color : "#CBD5E1" }}>
                          {sec.label}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 border tabular-nums font-bold" style={{
                          color: sec.color,
                          borderColor: `${sec.color}55`,
                          background: `${sec.color}15`,
                        }}>
                          {c.visible}{c.visible !== c.total && ` / ${c.total}`}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Description bar */}
                <div className="px-5 py-3 border-b border-white/[0.04] bg-[#0A0C10]">
                  {SECTIONS.filter(s => s.key.toLowerCase() === activeIntel).map(s => (
                    <p key={s.key} className="text-xs text-[#A0AEC0] leading-relaxed">
                      <span className="font-bold uppercase tracking-widest mr-2" style={{ color: s.color }}>{s.label}</span>
                      {s.descKey ? t(s.descKey) : s.description}
                    </p>
                  ))}
                </div>

                {/* Active panel */}
                <div className="p-5">
                  {activeIntel === "osint" && (
                    <OsintPanel data={intel.osint} filter={osintFilter} sort={sortFn} onChange={v => setIntel(p => ({ ...p, osint: v }))} />
                  )}
                  {activeIntel === "socmint" && (
                    <SocmintPanel data={intel.socmint} filter={socmintFilter} sort={sortFn} onChange={v => setIntel(p => ({ ...p, socmint: v }))} />
                  )}
                  {activeIntel === "humint" && (
                    <HumintPanel data={intel.humint} filter={humintFilter} sort={sortFn} onChange={v => setIntel(p => ({ ...p, humint: v }))} />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
